import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  Typography,
  Tooltip,
  styled,
  Modal,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  FiPlus,
  FiEdit2,
  FiEye,
  FiCheck,
  FiX,
  FiAlertTriangle,
  FiDollarSign,
  FiRepeat,
  FiFileText,
  FiAlertCircle
} from 'react-icons/fi';
import { TransactionServiceClient } from '../../services/client/TransactionServiceClient';
import { LedgerServiceClient } from '../../services/client/LedgerServiceClient';
import { TransactionDetailsDrawer } from './drawers/TransactionDetailsDrawer';
import AdvancedFilters from '../common/AdvancedFilters';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../utils/formatters';
import './JournalEntries.css';
import { useJournalEntries } from '../../hooks/useJournalEntries';
import JournalEntryForm from './forms/JournalEntryForm';

interface LineItem {
  id: string;
  accountId: string;
  debit: number;
  credit: number;
  description?: string;
  taxCategory?: string;
}

enum JournalEntryType {
  MANUAL = 'MANUAL',
  SYSTEM = 'SYSTEM',
  RECURRING = 'RECURRING',
  IMPORT = 'IMPORT'
}

interface FiscalCategory {
  causeId?: string;
  campaignId?: string;
  accountingCode?: string;
  taxCategory?: string;
  type?: 'PROGRAM' | 'ADMIN' | 'FUNDRAISING';
  fundType?: string;
  restrictionType?: string;
}

interface RecurringEntryOptions {
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  startDate: Date;
  endDate?: Date | null;
  dayOfMonth?: number;
  dayOfWeek?: number;
  monthOfYear?: number;
}

interface JournalEntryFromHook {
  id: string;
  date: Date | string;
  type: string;
  description: string;
  status: string;
  lineItems: LineItem[];
  fiscalCategory?: {
    cause?: string;
    campaign?: string;
    accountingCode?: string;
    taxCategory?: string;
    type?: 'PROGRAM' | 'ADMIN' | 'FUNDRAISING';
    fundType?: string;
    restrictionType?: string;
  };
  accountCategory?: string;
  subCategory?: string;
  accountCode?: string;
  taxCategory?: string;
  isRecurring?: boolean;
  documents?: DocumentType[];
  createdAt?: Date;
  updatedAt?: Date;
}

interface JournalEntry {
  id: string;
  date: Date | string;
  type: JournalEntryType;
  description: string;
  status: string;
  lineItems: LineItem[];
  fiscalCategory?: FiscalCategory;
  accountCategory?: string;
  subCategory?: string;
  accountCode?: string;
  taxCategory?: string;
  isRecurring?: boolean;
  documents?: DocumentType[];
  createdAt?: Date;
  updatedAt?: Date;
}

// Add styled TableCell component for headers
const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  color: '#3b9db8',
  fontWeight: 600,
  fontSize: '0.85rem',
  borderBottom: '2px solid #3b9db8',
  padding: '4px 2px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  lineHeight: 1.1,
  minWidth: 'auto',
  '& .header-label': {
    display: 'block',
    whiteSpace: 'nowrap',
  },
  '& .sub-label': {
    display: 'block',
    fontSize: '0.75rem',
    opacity: 0.9,
    whiteSpace: 'nowrap',
  }
}));

const DescriptionModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const DocumentsDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    minWidth: '500px',
    maxWidth: '800px'
  }
});

const StyledTable = styled(Table)({
  tableLayout: 'fixed',
  '& th': {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
});

// Add type for documents
type DocumentType = {
  id: string;
  name: string;
  url: string;
  type: string;
};

// Define the JournalEntryFormData type if it's not exported from accounting types
interface JournalEntryFormData {
  id?: string;
  date: Date;
  description: string;
  type: string;
  fiscalCategory: {
    causeId: string;
    campaignId: string;
    accountingCode: string;
    taxCategory: string;
    type: 'PROGRAM' | 'ADMIN' | 'FUNDRAISING';
    fundType: string;
    restrictionType: string;
  };
  lineItems: {
    id: string;
    accountId: string;
    debit: number;
    credit: number;
    description?: string;
    taxCategory?: string;
  }[];
}

// Define the JournalEntryResponse type
interface JournalEntryResponse {
  id: string;
  success: boolean;
  message?: string;
}

// Define the JournalEntryFilters interface
interface JournalEntryFilters {
  type: string;
  category: string;
  search: string;
  showInactive: boolean;
  taxCategory: string;
  fundType: string;
}

// Add a props interface for JournalEntriesTab
interface JournalEntriesTabProps {
  onEditEntry: (entry: JournalEntry) => void;
  onCreateEntry: () => void;
}

export const JournalEntriesTab: React.FC<JournalEntriesTabProps> = ({ 
  onEditEntry, 
  onCreateEntry 
}) => {
  const { entries, loading, error } = useJournalEntries();
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [filters, setFilters] = useState<JournalEntryFilters>({
    type: '',
    category: '',
    search: '',
    showInactive: false,
    taxCategory: '',
    fundType: ''
  });
  const [batchMode, setBatchMode] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const [selectedDescription, setSelectedDescription] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentType[] | null>(null);
  const [alertInfo, setAlertInfo] = useState<{ show: boolean; severity: 'success' | 'error'; message: string }>({ show: false, severity: 'success', message: '' });

  const { currentUser } = useAuth();

  const fetchEntries = () => {
    // This function would typically call a service to fetch entries
    // For now, we're using the useJournalEntries hook which handles this
    console.log('Refreshing entries');
    // If you need to manually refresh, you could call a refetch function from the hook
    // refetchEntries();
  };

  const handleEntryCreate = async (data: JournalEntry) => {
    try {
      // Transform the data to match the expected format for TransactionService.create
      await TransactionServiceClient.create({
        ...data,
        date: data.date instanceof Date ? data.date.toISOString() : data.date,
        fiscalSponsorId: currentUser.fiscalSponsorId,
        status: 'PENDING'
      });
      
      // Refresh the entries list
      fetchEntries();
      
      // Close the form
      setShowForm(false);
      
      // Show success message
      setAlertInfo({
        show: true,
        severity: 'success',
        message: 'Journal entry created successfully'
      });
    } catch (error) {
      console.error('Error creating entry:', error);
      setAlertInfo({
        show: true,
        severity: 'error',
        message: 'Failed to create journal entry'
      });
    }
  };

  const handleEntryApprove = async (entryId: string) => {
    try {
      await TransactionServiceClient.approve(entryId, currentUser.id);
      // Handle success state
    } catch (error) {
      console.error('Error approving entry:', error);
      // Handle error state
    }
  };

  const handleBulkApprove = async () => {
    try {
      await Promise.all(
        selectedEntries.map(id => TransactionServiceClient.approve(id, currentUser.id))
      );
      // Handle success state
    } catch (error) {
      console.error('Error approving entries:', error);
      // Handle error state
    }
  };

  const handleRecurringEntry = async (entry: JournalEntry) => {
    try {
      const recurringOptions: RecurringEntryOptions = {
        frequency: 'monthly', // Default to monthly, you might want to make this configurable
        startDate: new Date(),
        dayOfMonth: new Date().getDate()
      };
      
      await TransactionServiceClient.createRecurring({
        ...entry,
        date: entry.date instanceof Date ? entry.date.toISOString() : entry.date,
        fiscalSponsorId: currentUser.fiscalSponsorId,
        recurringOptions
      });
      
      // Handle success state
    } catch (error) {
      console.error('Error setting up recurring entry:', error);
      // Handle error state
    }
  };

  const handleFilterChange = (newFilters: Record<string, any>) => {
    setFilters((prev: JournalEntryFilters) => ({
      ...prev,
      ...newFilters
    }));
  };

  const handleEdit = () => {
    if (selectedEntry) {
      setSelectedEntry(selectedEntry);
      setShowForm(true);
    }
  };

  // Let's add a console.log to see the actual structure of the entries
  useEffect(() => {
    if (entries.length > 0) {
      console.log('Entry structure:', entries[0]);
      console.log('FiscalCategory structure:', entries[0].fiscalCategory);
    }
  }, [entries]);

  return (
    <Box className="journal-entries" sx={{ overflowX: 'auto' }}>
      <Box className="header-actions">
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FiPlus />}
              onClick={onCreateEntry}
            >
              Add Entry
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<FiRepeat />}
              onClick={() => {/* Handle recurring entries */}}
            >
              Recurring Entries
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => setBatchMode(!batchMode)}
            >
              Batch Operations
            </Button>
          </Grid>
        </Grid>
      </Box>

      <AdvancedFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        fields={[
          { name: 'dateRange', type: 'dateRange', label: 'Date Range' },
          { name: 'status', type: 'select', label: 'Status' },
          { name: 'type', type: 'select', label: 'Type' },
          { name: 'amount', type: 'numberRange', label: 'Amount Range' }
        ]}
      />

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <StyledTable className="journal-table" size="small">
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell width="65px">Date</StyledTableHeaderCell>
              <StyledTableHeaderCell width="50px">Type</StyledTableHeaderCell>
              <StyledTableHeaderCell width="45px">
                <FiFileText size={12} />
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="60px">Status</StyledTableHeaderCell>
              <StyledTableHeaderCell width="70px">
                <span className="header-label">Cause/</span>
                <span className="sub-label">Project</span>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="75px">
                <span className="header-label">Campaign/</span>
                <span className="sub-label">Program</span>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="55px">
                <span className="header-label">Fund</span>
                <span className="sub-label">Type</span>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="60px">
                <span className="header-label">Restrict</span>
                <span className="sub-label">Type</span>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="70px">
                <span className="header-label">Account</span>
                <span className="sub-label">Category</span>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="65px">
                <span className="header-label">Sub-</span>
                <span className="sub-label">Category</span>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="65px">
                <span className="header-label">Account</span>
                <span className="sub-label">Code</span>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="65px">
                <span className="header-label">990 Tax</span>
                <span className="sub-label">Category</span>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="40px">
                <FiRepeat size={12} />
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="40px">
                <FiFileText size={12} />
              </StyledTableHeaderCell>
              <StyledTableHeaderCell width="90px">Actions</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{new Date(entry.date).toLocaleDateString()}</TableCell>
                <TableCell>{entry.type}</TableCell>
                <TableCell>
                  <Tooltip title="View full description">
                    <IconButton 
                      size="small" 
                      onClick={() => setSelectedDescription(entry.description)}
                    >
                      <FiFileText size={12} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={entry.status}
                    size="small"
                    color={
                      entry.status === 'approved' ? 'success' :
                      entry.status === 'pending' ? 'warning' :
                      'error'
                    }
                  />
                </TableCell>
                <TableCell>{(entry.fiscalCategory as any)?.cause || '-'}</TableCell>
                <TableCell>{(entry.fiscalCategory as any)?.campaign || '-'}</TableCell>
                <TableCell>{entry.fiscalCategory?.fundType}</TableCell>
                <TableCell>{entry.fiscalCategory?.restrictionType}</TableCell>
                <TableCell>{entry.accountCategory}</TableCell>
                <TableCell>{entry.subCategory}</TableCell>
                <TableCell>{entry.accountCode}</TableCell>
                <TableCell>{entry.taxCategory}</TableCell>
                <TableCell>
                  {entry.isRecurring && (
                    <Tooltip title="Recurring Entry">
                      <FiRepeat size={12} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {entry.documents && entry.documents.length > 0 && (
                    <Tooltip title="View Documents">
                      <IconButton
                        size="small"
                        onClick={() => setSelectedDocuments(entry.documents as DocumentType[] || [])}
                      >
                        <FiFileText size={12} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <FiEye />
                      </IconButton>
                    </Tooltip>
                    {entry.status === 'pending' && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => onEditEntry(entry as unknown as JournalEntry)}
                            aria-label="Edit entry"
                          >
                            <FiEdit2 />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Approve">
                          <IconButton size="small">
                            <FiCheck />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton size="small">
                            <FiX />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      )}

      {/* Journal Entry Form Dialog */}
      <Dialog
        open={showForm}
        onClose={() => setShowForm(false)}
        maxWidth="md"
        fullWidth
      >
        <JournalEntryForm
          initialData={selectedEntry ? {
            id: selectedEntry.id,
            date: new Date(selectedEntry.date),
            description: selectedEntry.description,
            type: selectedEntry.type,
            fiscalCategory: {
              causeId: (selectedEntry.fiscalCategory as any)?.cause || '',
              campaignId: (selectedEntry.fiscalCategory as any)?.campaign || '',
              accountingCode: selectedEntry.fiscalCategory?.accountingCode || '',
              taxCategory: selectedEntry.fiscalCategory?.taxCategory || '',
              type: selectedEntry.fiscalCategory?.type || 'PROGRAM',
              fundType: selectedEntry.fiscalCategory?.fundType || '',
              restrictionType: selectedEntry.fiscalCategory?.restrictionType || ''
            },
            lineItems: selectedEntry.lineItems.map((item, index) => ({
              id: item.id || `temp-${index}`,
              accountId: item.accountId,
              debit: item.debit || 0,
              credit: item.credit || 0,
              description: item.description || '',
              taxCategory: item.taxCategory || ''
            }))
          } as any : undefined}
          onSubmit={(values: any) => {
            // Convert the values to the format expected by handleEntryCreate
            const formData = {
              id: values.id,
              date: values.date instanceof Date ? values.date : new Date(values.date),
              description: values.description,
              type: values.type,
              fiscalCategory: {
                causeId: values.fiscalCategory?.causeId || '',
                campaignId: values.fiscalCategory?.campaignId || '',
                accountingCode: values.fiscalCategory?.accountingCode || '',
                taxCategory: values.fiscalCategory?.taxCategory || '',
                type: values.fiscalCategory?.type || 'PROGRAM',
                fundType: values.fiscalCategory?.fundType || '',
                restrictionType: values.fiscalCategory?.restrictionType || ''
              },
              lineItems: values.lineItems.map((item: any) => ({
                id: item.id,
                accountId: item.accountId,
                debit: item.debit,
                credit: item.credit,
                description: item.description,
                taxCategory: item.taxCategory
              }))
            };
            handleEntryCreate(formData as any);
          }}
          onCancel={() => setShowForm(false)}
        />
      </Dialog>

      {/* Transaction Details Drawer */}
      <TransactionDetailsDrawer
        open={showDetails}
        transaction={selectedEntry}
        onClose={() => setShowDetails(false)}
        onEdit={handleEdit}
      />

      {/* Description Modal */}
      <DescriptionModal
        open={!!selectedDescription}
        onClose={() => setSelectedDescription(null)}
      >
        <Paper sx={{ p: 4, maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Description
          </Typography>
          <Typography>
            {selectedDescription}
          </Typography>
        </Paper>
      </DescriptionModal>

      {/* Documents Dialog */}
      <DocumentsDialog
        open={!!selectedDocuments && selectedDocuments.length > 0}
        onClose={() => setSelectedDocuments(null)}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Associated Documents
          </Typography>
          {selectedDocuments?.map((doc, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                {doc.type}: {doc.name}
              </Typography>
              {doc.url && (
                <img 
                  src={doc.url} 
                  alt={doc.name}
                  style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                />
              )}
            </Box>
          ))}
        </Box>
      </DocumentsDialog>
    </Box>
  );
};

export default JournalEntriesTab; 